// DashboardPage v2 — operação ao vivo com KPIs, técnicos no mapa e painel de atenção.
// Princípio: "Operação em 5 segundos" — olha a tela e sabe se tá tudo bem.

import { useEffect, useState, useMemo } from 'react';
import GoogleMap from '../components/GoogleMap.jsx';
import HealthBar from '../components/HealthBar.jsx';
import BarraTecnicos from '../components/BarraTecnicos.jsx';
import PainelAtencao from '../components/PainelAtencao.jsx';
import LegendaStatusVisita from '../components/LegendaStatusVisita.jsx';
import TecnicoVisitasModal from '../components/TecnicoVisitasModal.jsx';
import BuscaOS, { visitaCasaBusca } from '../components/BuscaOS.jsx';
import { montarTextoAtencao } from '../lib/visita.js';
import { dashboard } from '../lib/api.js';
import { gerarAvatarSvg, gerarPinVisitaSvg } from '../lib/avatarSvg.js';
import { osLabel } from '../lib/visita.js';

const FLORIPA_CENTRO = [-27.595, -48.55];

export default function DashboardPage() {
  const [snap, setSnap] = useState(null);
  const [erro, setErro] = useState(null);
  const [filtroChip, setFiltroChip] = useState(null);   // filtro vindo da HealthBar
  const [tecnicoSelecionado, setTecnicoSelecionado] = useState(null);
  const [tecnicoModalAberto, setTecnicoModalAberto] = useState(null);
  const [busca, setBusca] = useState('');
  const [mapCenter, setMapCenter] = useState(FLORIPA_CENTRO);
  const [mapZoom, setMapZoom] = useState(11);

  async function recarregar() {
    try {
      const r = await dashboard.snapshot();
      setSnap(r);
      setErro(null);
    } catch (e) {
      setErro(e.uiMessage || 'Erro ao carregar dashboard');
    }
  }

  useEffect(() => {
    recarregar();
    const h = setInterval(recarregar, 30_000);
    return () => clearInterval(h);
  }, []);

  // Resumo da busca (encontradas vs total)
  const resumoBusca = useMemo(() => {
    if (!snap || !busca || !busca.trim()) return null;
    const total = snap.visitas.length;
    const encontradas = snap.visitas.filter(v => visitaCasaBusca(v, busca)).length;
    return { total, encontradas };
  }, [snap, busca]);

  // Visitas filtradas pela busca (alimenta o PainelAtencao à direita)
  const visitasFiltradas = useMemo(() => {
    if (!snap) return [];
    if (!busca || !busca.trim()) return snap.visitas;
    return snap.visitas.filter(v => visitaCasaBusca(v, busca));
  }, [snap, busca]);

  // Itens de atenção filtrados (cada um referencia uma visita_id —
  // só mantém os que apontam pra visita que casa com a busca).
  const atencaoFiltrada = useMemo(() => {
    if (!snap) return [];
    const atencao = snap.atencao || [];
    if (!busca || !busca.trim()) return atencao;
    const idsFiltradas = new Set(visitasFiltradas.map(v => String(v.id)));
    return atencao.filter(a => idsFiltradas.has(String(a.visita_id)));
  }, [snap, busca, visitasFiltradas]);

  // Técnicos filtrados pela busca (alimenta a BarraTecnicos)
  const tecnicosFiltrados = useMemo(() => {
    if (!snap) return [];
    const tecnicos = snap.tecnicos || [];
    if (!busca || !busca.trim()) return tecnicos;
    const q = busca.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const ids = new Set(tecnicos
      .filter(t =>
        (t.apelido || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(q) ||
        (t.nome    || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(q)
      )
      .map(t => String(t.id)));
    for (const v of visitasFiltradas) {
      if (v.tecnico_id) ids.add(String(v.tecnico_id));
    }
    return tecnicos.filter(t => ids.has(String(t.id)));
  }, [snap, busca, visitasFiltradas]);

  // Markers do mapa: pins de visitas + avatares de técnicos
  const markers = useMemo(() => {
    if (!snap) return [];
    const list = [];

    // 1) Pins das visitas (apenas com lat/lon)
    for (const v of snap.visitas) {
      if (!v.cliente_lat || !v.cliente_lon) continue;
      // Filtro opcional vindo da HealthBar
      if (filtroChip && v.status_visual !== filtroChip) continue;
      // Filtro de busca por protocolo / cliente
      if (!visitaCasaBusca(v, busca)) continue;
      list.push({
        lat: Number(v.cliente_lat),
        lng: Number(v.cliente_lon),
        iconUrl: gerarPinVisitaSvg(v.status_visual),
        iconSize: v.status_visual === 'atrasada' ? 28 : 22,
        zIndex: v.status_visual === 'atrasada' ? 800 : 500,
        label: `OS ${osLabel(v)}`,
        info: `<div style="min-width:200px">
          <strong>OS ${osLabel(v)}</strong> · ${v.status_visual}<br/>
          ${v.cliente_nome || ''}<br/>
          ${v.cliente_bairro || ''}<br/>
          ${v.tecnico_apelido || '(sem técnico)'} · ${v.tipo_os || ''}<br/>
          <a href="/visitas/${v.id}">Ver detalhe →</a>
        </div>`,
      });
    }

    // 2) Avatares dos técnicos (em cima dos pins)
    for (const t of snap.tecnicos) {
      if (!t.posicao || !t.posicao.lat) continue;
      const proxTxt = t.proxima_visita
        ? `Próxima: OS ${osLabel(t.proxima_visita)} · ${t.proxima_visita.cliente_nome || ''}`
        : 'Sem próxima visita';
      list.push({
        lat: Number(t.posicao.lat),
        lng: Number(t.posicao.lon),
        iconUrl: gerarAvatarSvg(t.iniciais, t.avatar_cor, t.status_calc),
        iconSize: t.status_calc === 'parado_alerta' ? 56 : 48,
        zIndex: 1000,
        label: t.apelido,
        info: `<div style="min-width:200px">
          <strong>${t.apelido || t.nome}</strong> · ${t.status_calc.replace('_', ' ')}<br/>
          ${t.veiculo_placa || ''}<br/>
          Visitas hoje: ${t.visitas_feitas}/${t.visitas_total}<br/>
          ${proxTxt}<br/>
          <em style="font-size:11px;color:#6b7280">📋 clique no avatar pra ver todas as OS</em>
        </div>`,
        onClick: () => setTecnicoModalAberto(t),
      });
    }

    return list;
  }, [snap, filtroChip, busca]);

  function onClickTecnico(t) {
    setTecnicoSelecionado(t.id === tecnicoSelecionado ? null : t.id);
    setTecnicoModalAberto(t);  // abre modal com OS do técnico
    if (t.posicao && t.posicao.lat) {
      setMapCenter([Number(t.posicao.lat), Number(t.posicao.lon)]);
      setMapZoom(14);
    }
  }

  return (
    <div className="stack">
      {erro && <div className="card error">{erro}</div>}

      <HealthBar
        resumo={snap?.resumo}
        atencao={snap?.atencao || []}
        mostrarBanner={false}
        ts={snap?.meta?.ts}
        meta={snap?.meta}
        filtroAtivo={filtroChip}
        onFiltroChip={setFiltroChip}
      />

      {/* Linha: busca à esquerda + banner de atenção à direita (libera o topo pro mapa) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <BuscaOS value={busca} onChange={setBusca} resumo={resumoBusca} />
        {(() => {
          const atencao = atencaoFiltrada || [];
          const texto = montarTextoAtencao(atencao);
          if (!texto) return <div style={{ flex: 1 }} />;
          return (
            <div style={{
              flex: 1,
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 12,
              color: '#991b1b',
              minWidth: 0,
            }} title={texto}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
              <span style={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                <strong>{atencao.length} {atencao.length === 1 ? 'OS precisa' : 'OS precisam'} atenção</strong>
                {' · '}{texto}
              </span>
            </div>
          );
        })()}
      </div>

      <div className="dashboard-grid">
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <GoogleMap
            center={mapCenter}
            zoom={mapZoom}
            height={540}
            markers={markers}
          />
          <LegendaStatusVisita
            resumo={snap?.resumo}
            statusSelecionado={filtroChip}
            onChange={setFiltroChip}
          />
        </div>

        <PainelAtencao
          atencao={atencaoFiltrada}
          visitas={visitasFiltradas}
          tecnicos={snap?.tecnicos || []}
          filtroExterno={filtroChip}
        />
      </div>

      <BarraTecnicos
        tecnicos={tecnicosFiltrados}
        onClickTecnico={onClickTecnico}
        tecnicoSelecionado={tecnicoSelecionado}
      />

      {/* Modal de OS do técnico ao clicar no avatar */}
      {tecnicoModalAberto && (
        <TecnicoVisitasModal
          tecnico={tecnicoModalAberto}
          visitas={(snap?.visitas || []).filter(v => v.tecnico_id === tecnicoModalAberto.id)}
          onClose={() => setTecnicoModalAberto(null)}
        />
      )}
    </div>
  );
}
